import { ArrowDown, ArrowUp, BookOpen, ChevronDown, Dumbbell, Info, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import useAtelierCreator from '../../stores/useAtelierCreator';

// ── TOKENS
const tokens = (dm) => ({
  textSub: dm ? 'text-zinc-400' : 'text-zinc-500',
  textMuted: dm ? 'text-zinc-600' : 'text-zinc-400',
  input: dm
    ? 'bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-600 focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/20'
    : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 shadow-sm',
}); 

function FieldLabel({ label, hint, required, darkMode }) {
  return (
    <div className="mb-1.5">
      <div className="flex items-center gap-1.5">
        <label className={`text-[11px] font-bold uppercase tracking-widest ${darkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>{label}</label>
        {required && <span className="text-[9px] font-bold text-red-400 border border-red-400/30 px-1 rounded-sm">requis</span>}
      </div>
      {hint && <p className={`text-[11px] mt-0.5 leading-snug ${darkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{hint}</p>}
    </div>
  );
}

function WorkspaceCard({ title, subtitle, icon: Icon, accent = 'cyan', required = false, children, darkMode, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const dm = darkMode;
  const accentColors = { cyan: { border: 'border-l-cyan-400', icon: 'text-cyan-500' }, emerald: { border: 'border-l-emerald-400', icon: 'text-emerald-500' } };
  const ac = accentColors[accent] || accentColors.cyan;

  return (
    <div className={`rounded-lg border transition-all overflow-hidden ${open ? `${ac.border} border-l-2 border-y ${dm ? 'border-white/8 bg-zinc-900/40' : 'border-zinc-200 bg-white shadow-sm'}` : `border-l-2 border-l-transparent border-y ${dm ? 'border-white/5' : 'border-zinc-100'}`}`}>
      <button type="button" onClick={() => setOpen(v => !v)} className={`w-full flex items-center justify-between px-5 py-4 transition-colors ${dm ? 'hover:bg-white/3' : 'hover:bg-zinc-50/70'}`}>
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 p-1.5 rounded-md ${dm ? 'bg-zinc-800' : 'bg-zinc-100'}`}><Icon size={14} className={open ? ac.icon : dm ? 'text-zinc-500' : 'text-zinc-400'} /></div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className={`text-[13px] font-semibold ${open ? (dm ? 'text-white' : 'text-zinc-900') : (dm ? 'text-zinc-500' : 'text-zinc-500')}`}>{title}</span>
              {required && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${open ? 'text-red-400 border-red-400/30 bg-red-400/5' : 'text-zinc-500 border-zinc-600'}`}>REQUIS</span>}
            </div>
            {subtitle && open && <p className={`text-[11px] mt-0.5 ${dm ? 'text-zinc-500' : 'text-zinc-400'}`}>{subtitle}</p>}
          </div>
        </div>
        <ChevronDown size={14} className={`flex-shrink-0 transition-transform ${dm ? 'text-zinc-600' : 'text-zinc-400'} ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className={`px-5 pb-5 border-t ${dm ? 'border-white/5' : 'border-zinc-100'}`}><div className="pt-4">{children}</div></div>}
    </div>
  );
}

const EXERCISE_TYPES = ['writing', 'brainstorm', 'grid', 'strategy', 'creative', 'collaborative', 'guided', 'role-play', 'case-study', 'discussion'];

export default function Section2_Contenu({ darkMode }) {
  const dm = darkMode;
  const tk = tokens(dm);
  const inputCls = `${tk.input} border outline-none transition-all rounded-lg px-3 py-2.5 text-[13px] w-full`;

  const { formData, errors, updateFormData, addSlide, updateSlide, removeSlide, moveSlide, addExercise, updateExercise, removeExercise, interfaceMode } = useAtelierCreator();
  const isSimple = interfaceMode === 'simplifie';

  const slides = formData.theory?.slides || [];
  const exercises = formData.exercises || [];

  return (
    <div className="space-y-4 animate-in fade-in duration-500">

      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold tracking-tight ${dm ? 'text-white' : 'text-zinc-900'}`}>
            {isSimple ? 'Contenu de l\'atelier' : 'Séquence pédagogique'}
          </h2>
          <p className={`text-[12px] mt-1 ${tk.textSub}`}>
            {isSimple ? 'Théorie et exercices pratiques' : 'Structure narrative et activités'}
          </p>
        </div>
        <div className={`text-[10px] font-mono px-2 py-1 rounded border ${dm ? 'border-zinc-700 text-zinc-500' : 'border-zinc-200 text-zinc-400'}`}>
          {isSimple ? 'MODE SIMPLIFIÉ' : 'MODE EXPERT'}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        
        {/* Colonne gauche — Théorie */}
        <div className="col-span-12 lg:col-span-7 space-y-4">
          <WorkspaceCard title="Module théorique" subtitle="Slides et contenu pédagogique" icon={BookOpen} accent="cyan" required darkMode={dm} defaultOpen>
            <div className="space-y-4">
              <FieldLabel label="Titre du module" hint="Nom de la section théorique" required darkMode={dm} />
              <input value={formData.theory?.title || ''} onChange={e => updateFormData('theory.title', e.target.value)} className={inputCls} placeholder="Les grandes filières post-bac" />
              {errors?.theoryTitle && <p className="text-red-400 text-[11px] mt-1">{errors.theoryTitle}</p>}

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className={`text-[11px] font-semibold ${tk.textSub}`}>Slides ({slides.length})</span>
                  <button type="button" onClick={addSlide} className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-md border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"><Plus size={11} /> Ajouter</button>
                </div>
                {slides.map((slide, idx) => (
                  <div key={idx} className={`rounded-lg border p-4 ${dm ? 'border-white/5 bg-black/20' : 'border-zinc-200 bg-zinc-50'}`}>
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-md flex items-center justify-center ${dm ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                          <span className={`text-[10px] font-black ${dm ? 'text-cyan-400' : 'text-cyan-700'}`}>{idx + 1}</span>
                        </div>
                        <span className={`text-[10px] font-mono uppercase ${tk.textMuted}`}>Slide_{String(idx + 1).padStart(2, '0')}</span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => moveSlide(idx, -1)} disabled={idx === 0} className={`p-1 transition-colors ${dm ? 'text-zinc-500 hover:text-cyan-400' : 'text-zinc-400 hover:text-cyan-600'} disabled:opacity-20`}><ArrowUp size={12} /></button>
                        <button onClick={() => moveSlide(idx, 1)} disabled={idx === slides.length - 1} className={`p-1 transition-colors ${dm ? 'text-zinc-500 hover:text-cyan-400' : 'text-zinc-400 hover:text-cyan-600'} disabled:opacity-20`}><ArrowDown size={12} /></button>
                        <button onClick={() => removeSlide(idx)} className={`p-1 transition-colors ml-1 ${dm ? 'text-zinc-500 hover:text-red-400' : 'text-zinc-400 hover:text-red-500'}`}><Trash2 size={12} /></button>
                      </div>
                    </div>
                    <input value={slide.title} onChange={e => updateSlide(idx, 'title', e.target.value)} className={`${inputCls} mb-2`} placeholder="Titre du slide" />
                    <textarea value={slide.content} onChange={e => updateSlide(idx, 'content', e.target.value)} rows={3} className={`${inputCls} resize-none`} placeholder="Contenu du slide..." />
                  </div>
                ))}
                {slides.length === 0 && (
                  <div className={`p-8 text-center rounded-lg border border-dashed ${dm ? 'border-white/5' : 'border-zinc-200'}`}>
                    <p className={`text-[11px] font-mono uppercase ${tk.textMuted}`}>Aucun slide — cliquez sur "Ajouter"</p>
                  </div>
                )}
              </div>
            </div>
          </WorkspaceCard>
        </div>

        {/* Colonne droite — Exercices */}
        <div className="col-span-12 lg:col-span-5 space-y-4">
          <WorkspaceCard title="Exercices" subtitle="Activités pratiques" icon={Dumbbell} accent="emerald" required darkMode={dm} defaultOpen>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className={`text-[11px] font-semibold ${tk.textSub}`}>Activités ({exercises.length})</span>
                <button type="button" onClick={addExercise} className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-md border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"><Plus size={11} /> Ajouter</button>
              </div>
              {exercises.map((ex, idx) => (
                <div key={ex.id || idx} className={`rounded-lg border p-4 ${dm ? 'border-white/5 bg-black/20' : 'border-zinc-200 bg-zinc-50'}`}>
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center ${dm ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                        <span className={`text-[10px] font-black ${dm ? 'text-emerald-400' : 'text-emerald-700'}`}>{idx + 1}</span>
                      </div>
                      <span className={`text-[10px] font-mono uppercase ${tk.textMuted}`}>Ex_{String(idx + 1).padStart(2, '0')}</span>
                    </div>
                    <button onClick={() => removeExercise(idx)} className={`p-1 transition-colors ${dm ? 'text-zinc-500 hover:text-red-400' : 'text-zinc-400 hover:text-red-500'}`}><Trash2 size={12} /></button>
                  </div>
                  <div className="space-y-3">
                    <input value={ex.title} onChange={e => updateExercise(idx, 'title', e.target.value)} className={inputCls} placeholder="Titre de l'exercice" />
                    <select value={ex.type} onChange={e => updateExercise(idx, 'type', e.target.value)} className={inputCls}>
                      {EXERCISE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <textarea value={ex.prompt} onChange={e => updateExercise(idx, 'prompt', e.target.value)} rows={3} className={`${inputCls} resize-none`} placeholder="Consigne de l'exercice..." />
                  </div>
                </div>
              ))}
              {exercises.length === 0 && (
                <div className={`p-8 text-center rounded-lg border border-dashed ${dm ? 'border-white/5' : 'border-zinc-200'}`}>
                  <p className={`text-[11px] font-mono uppercase ${tk.textMuted}`}>Aucun exercice — cliquez sur "Ajouter"</p>
                </div>
              )}
            </div>
          </WorkspaceCard>
        </div>
      </div>

      {isSimple && (
        <div className={`p-4 rounded-xl border flex items-start gap-3 ${dm ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-cyan-50 border-cyan-200'}`}>
          <Info size={14} className="text-cyan-400 flex-shrink-0 mt-0.5" />
          <p className={`text-[12px] leading-relaxed ${dm ? 'text-cyan-300/80' : 'text-cyan-700'}`}>
            <strong>Conseil :</strong> Structurez votre contenu en slides, puis ajoutez des exercices pour ancrer les apprentissages.
          </p>
        </div>
      )}
    </div>
  );
}